/**
 * use2FASetup
 *
 * Manages the 2FA enrollment flow:
 *  1. Call /auth/2fa/setup → receive QR code + manual key
 *  2. User scans QR in authenticator app
 *  3. User types first 6-digit code → call /auth/2fa/verify → 2FA enabled
 *
 * State machine: idle → loading_qr → awaiting_code → verifying → enabled | error
 *
 * Extension point: add disable2FA() call to /auth/2fa/disable when implemented.
 */

import { useState, useCallback } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { api2FASetup, api2FAVerify } from '../api/auth';
import { ApiError } from '../api/client';

type SetupPhase = 'idle' | 'loading_qr' | 'awaiting_code' | 'verifying' | 'enabled';

export interface Use2FASetupReturn {
  phase: SetupPhase;
  qrCodeUrl: string | null;
  manualKey: string | null;
  code: string;
  error: string | null;
  setCode: (v: string) => void;
  startSetup: () => void;
  submitCode: () => void;
  reset: () => void;
}

export function use2FASetup(): Use2FASetupReturn {
  const updateUser    = useAuthStore((s) => s.updateUser);
  const user          = useAuthStore((s) => s.user);

  const [phase,     setPhase]     = useState<SetupPhase>('idle');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [manualKey, setManualKey] = useState<string | null>(null);
  const [code,      setCode]      = useState('');
  const [error,     setError]     = useState<string | null>(null);

  const startSetup = useCallback(() => {
    setPhase('loading_qr');
    setError(null);

    api2FASetup()
      .then((res) => {
        setQrCodeUrl(res.qrCodeUrl);
        setManualKey(res.manualKey);
        setPhase('awaiting_code');
      })
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Failed to start 2FA setup — try again',
        );
        setPhase('idle');
      });
  }, []);

  const submitCode = useCallback(() => {
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }

    setPhase('verifying');
    setError(null);

    api2FAVerify({ code })
      .then(() => {
        setPhase('enabled');
        // Reflect the change in Zustand without a full /me round-trip
        if (user) updateUser({ ...user, twoFactorEnabled: true });
      })
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError && err.status === 401
            ? 'Incorrect code — try again'
            : 'Verification failed — try again',
        );
        setPhase('awaiting_code');
      });
  }, [code, user, updateUser]);

  const reset = useCallback(() => {
    setPhase('idle');
    setQrCodeUrl(null);
    setManualKey(null);
    setCode('');
    setError(null);
  }, []);

  return { phase, qrCodeUrl, manualKey, code, error, setCode, startSetup, submitCode, reset };
}
